import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !project) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Eye' },
    { id: 'technical', label: 'Technical', icon: 'Code' },
    { id: 'impact', label: 'Impact', icon: 'TrendingUp' },
    { id: 'gallery', label: 'Gallery', icon: 'Image' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative">
          <div className="h-64 overflow-hidden">
            <Image
              src={project?.image}
              alt={project?.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent" />
          </div>
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors duration-300"
          >
            <Icon name="X" size={20} />
          </button>

          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">{project?.title}</h2>
                <div className="flex items-center space-x-4">
                  <span className="text-accent font-medium">{project?.category}</span>
                  <span className="text-white/80">{project?.year}</span>
                  {project?.status && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project?.status === 'Live' ?'bg-success text-white' 
                        : project?.status === 'In Development' ?'bg-warning text-white' :'bg-muted text-muted-foreground'
                    }`}>
                      {project?.status}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex space-x-3">
                {project?.liveUrl && (
                  <Button
                    variant="default"
                    iconName="ExternalLink"
                    iconPosition="right"
                    className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
                  >
                    View Live
                  </Button>
                )}
                {project?.githubUrl && (
                  <Button
                    variant="outline"
                    iconName="Github"
                    iconPosition="right"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Source Code
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-colors duration-300 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="font-medium">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Project Overview</h3>
                <p className="text-muted-foreground leading-relaxed">{project?.fullDescription}</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">Challenge</h4>
                <p className="text-muted-foreground leading-relaxed">{project?.challenge}</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">Solution</h4>
                <p className="text-muted-foreground leading-relaxed">{project?.solution}</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">Technologies Used</h4>
                <div className="flex flex-wrap gap-2">
                  {project?.technologies?.map((tech, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-md font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'technical' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Architecture Overview</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">{project?.architecture}</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">Key Implementation</h4>
                <div className="bg-muted rounded-lg p-4">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{project?.codeSnippet}</code>
                  </pre>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">Technical Highlights</h4>
                <ul className="space-y-2">
                  {project?.technicalHighlights?.map((highlight, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'impact' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Project Impact</h3>
                <p className="text-muted-foreground leading-relaxed">{project?.impactDescription}</p>
              </div>

              {project?.metrics && (
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-3">Key Metrics</h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(project?.metrics)?.map(([key, value]) => (
                      <div key={key} className="text-center p-4 bg-muted rounded-lg">
                        <div className="text-2xl font-bold text-primary mb-1">{value}</div>
                        <div className="text-sm text-muted-foreground capitalize">
                          {key?.replace(/([A-Z])/g, ' $1')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {project?.testimonial && (
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-3">Client Testimonial</h4>
                  <div className="bg-primary/5 border-l-4 border-primary p-4 rounded-r-lg">
                    <p className="text-muted-foreground italic mb-2">"{project?.testimonial?.quote}"</p>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {project?.testimonial?.author?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{project?.testimonial?.author}</div>
                        <div className="text-sm text-muted-foreground">{project?.testimonial?.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Project Gallery</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project?.gallery?.map((image, index) => (
                    <div key={index} className="rounded-lg overflow-hidden">
                      <Image
                        src={image?.url}
                        alt={image?.caption}
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      />
                      <div className="p-3 bg-muted">
                        <p className="text-sm text-muted-foreground">{image?.caption}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;